/**
 * Stability watchdogs — three narrow recovery loops for the classes of
 * silent playback failure our field forensics keep surfacing:
 *
 *   1. `startPlaybackWatchdog` — YouTube IFrame goes to PAUSED without a
 *      user gesture (network stutter, ad-block interception, YT throttling).
 *      Polls the driver every 5s; when the player is PAUSED but our
 *      MusicContext state says `isPlaying=true` AND the last progress tick
 *      was <10s ago (i.e. we WERE just playing), issues a `playVideo()`
 *      and logs `auto_resume` to forensics. After 3 failed resumes on the
 *      SAME videoId, gives up on that track — logs `unplayable_auto_skip`
 *      and calls `onAutoSkip()` so the queue advances instead of dying.
 *
 *   2. `startWakeLockLossLogger` — records tab-hidden→visible transitions
 *      that happened while music was supposed to be playing. Doesn't
 *      re-acquire the wake lock itself (useScreenWakeLock.js already
 *      handles that on `visibilitychange`); this loop is purely
 *      instrumentation so we can chart wake-lock churn per venue in
 *      Forensics and correlate it with the `auto_resume` events.
 *
 *   3. `startNetworkRecovery` — listens for `window.online`. If the player
 *      is stuck in {PAUSED, BUFFERING, UNSTARTED, CUED} while our state
 *      says `isPlaying=true`, force a `playVideo()` and log
 *      `net_recovered_resume`. Covers the "wi-fi hiccup mid-track, tab
 *      never recovers" hang.
 *
 * All three log through `/api/telemetry/playback-stall` (existing endpoint
 * accepts arbitrary `reason` + `meta`) so events flow into the same
 * Forensics UI we already use.
 *
 * Each starter returns its unsubscribe function; MusicContext wires them
 * up once after the driver is ready.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { createBackgroundInterval } from './backgroundTimer';

// YouTube IFrame state constants (per the API docs)
const YT_STATE_UNSTARTED = -1;
const YT_STATE_ENDED = 0;
const YT_STATE_PLAYING = 1;
const YT_STATE_PAUSED = 2;
const YT_STATE_BUFFERING = 3;
const YT_STATE_CUED = 5;

// Force-resume-eligible states — the player thinks it's "not playing right
// now" but our intent says it should be. Excludes ENDED (that's a legit
// track-done signal handled elsewhere) and PLAYING (nothing to fix).
const RESUMABLE_STATES = new Set([
  YT_STATE_UNSTARTED,
  YT_STATE_PAUSED,
  YT_STATE_BUFFERING,
  YT_STATE_CUED,
]);

const logStall = (reason, meta = {}, hostId = null) => {
  try {
    const body = JSON.stringify({
      reason,
      meta,
      host_id: hostId || null,
      user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
      is_hidden: typeof document !== 'undefined' && document.visibilityState !== 'visible',
      at: new Date().toISOString(),
    });
    // fetch + keepalive so the request survives a tab-hidden transition.
    fetch(`${API}/telemetry/playback-stall`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body,
    }).catch(() => { /* telemetry never crashes playback */ });
  } catch { /* noop */ }
};

const readPlayerState = (driver) => {
  try {
    const p = driver?._activePlayer?.();
    if (!p || typeof p.getPlayerState !== 'function') return YT_STATE_UNSTARTED;
    return p.getPlayerState();
  } catch {
    return YT_STATE_UNSTARTED;
  }
};

const forcePlay = (driver) => {
  try {
    const p = driver?._activePlayer?.();
    if (p && typeof p.playVideo === 'function') p.playVideo();
  } catch { /* noop */ }
};

// Re-kick a player wedged in BUFFERING: seek to the same spot (forces YT to
// re-request the segment) and play. Cheaper than a full reload and usually
// unsticks a slow/interrupted buffer.
const nudgeBuffer = (driver) => {
  try {
    const p = driver?._activePlayer?.();
    if (!p) return;
    const t = typeof p.getCurrentTime === 'function' ? (p.getCurrentTime() || 0) : 0;
    if (typeof p.seekTo === 'function') p.seekTo(t, true);
    if (typeof p.playVideo === 'function') p.playVideo();
  } catch { /* noop */ }
};

// ---------------------------------------------------------------------------
// 1. Mid-stream pause watchdog (medium — includes per-track failed-resume
//    tracking + auto-skip after 3 strikes)
// ---------------------------------------------------------------------------

const PAUSE_GRACE_MS = 4_000;         // ignore pauses shorter than this
const PROGRESS_STALE_MS = 10_000;     // only auto-resume if we were playing recently
const MAX_RESUMES_PER_TRACK = 3;      // after this, give up + skip
const BUFFER_STALL_MS = 12_000;       // frozen in BUFFERING this long => wedged
const BUFFER_NUDGE_INTERVAL_MS = 6_000; // min gap between buffer nudges

export function startPlaybackWatchdog({ driver, getState, onAutoSkip }) {
  if (!driver || typeof getState !== 'function') return () => {};

  // videoId -> failed resume attempts. Cleared when a new videoId becomes
  // active (see `activeVid` reset below).
  const failedResumes = new Map();
  const bufferStrikes = new Map();    // videoId -> buffer-nudge attempts
  let lastBufferNudgeAt = 0;

  let lastProgressMs = 0;
  let lastProgressAt = Date.now();
  let pausedSince = 0;
  let activeVid = null;

  const tick = () => {
    const state = getState() || {};
    const vid = state.currentTrack?.id || null;

    // Track change resets the per-track counter so a new track gets a fresh
    // 3-strike budget.
    if (vid !== activeVid) {
      activeVid = vid;
      pausedSince = 0;
      lastProgressMs = 0;
      lastProgressAt = Date.now();
      bufferStrikes.clear();
    }
    if (!state.isPlaying || !vid) {
      pausedSince = 0;
      return;
    }
    // Freshen the "we were making progress" timestamp whenever progress
    // ticks. If progress hasn't moved for >10s AND the player is paused,
    // that's a long-idle pause (likely operator intent, not a stutter),
    // so we DON'T auto-resume.
    if (typeof state.progressMs === 'number' && state.progressMs !== lastProgressMs) {
      lastProgressMs = state.progressMs;
      lastProgressAt = Date.now();
    }

    const playerState = readPlayerState(driver);

    // Frozen-BUFFERING recovery (slow net / brief cutout). YouTube can sit in
    // BUFFERING indefinitely with its progress clock frozen — the PAUSED
    // branch below never catches that. Nudge (seek-in-place + play); after
    // MAX_RESUMES_PER_TRACK nudges on the same track, hard-skip it.
    if (playerState === YT_STATE_BUFFERING) {
      pausedSince = 0;
      const frozenFor = Date.now() - lastProgressAt;
      if (frozenFor < BUFFER_STALL_MS) return;
      if (Date.now() - lastBufferNudgeAt < BUFFER_NUDGE_INTERVAL_MS) return;
      lastBufferNudgeAt = Date.now();
      const attempts = (bufferStrikes.get(vid) || 0) + 1;
      bufferStrikes.set(vid, attempts);
      if (attempts > MAX_RESUMES_PER_TRACK) {
        logStall('buffer_stall_skip', {
          video_id: vid,
          frozen_ms: frozenFor,
          nudges: attempts - 1,
          track_name: (state.currentTrack?.name || '').slice(0, 120),
        }, state.host_id);
        bufferStrikes.delete(vid);
        try { onAutoSkip?.(); } catch { /* noop */ }
        return;
      }
      logStall('buffer_stall_recover', {
        video_id: vid,
        frozen_ms: frozenFor,
        attempt: attempts,
        track_name: (state.currentTrack?.name || '').slice(0, 120),
      }, state.host_id);
      nudgeBuffer(driver);
      return;
    }

    if (playerState !== YT_STATE_PAUSED) {
      pausedSince = 0;
      return;
    }
    if (pausedSince === 0) {
      pausedSince = Date.now();
      return;   // wait one more tick to clear the grace window
    }
    const pausedFor = Date.now() - pausedSince;
    if (pausedFor < PAUSE_GRACE_MS) return;
    const sinceProgress = Date.now() - lastProgressAt;
    if (sinceProgress > PROGRESS_STALE_MS) return;

    // Silent pause. Time to intervene.
    const attempts = (failedResumes.get(vid) || 0) + 1;
    failedResumes.set(vid, attempts);

    if (attempts > MAX_RESUMES_PER_TRACK) {
      logStall('unplayable_auto_skip', {
        video_id: vid,
        resume_attempts: attempts - 1,
        paused_ms: pausedFor,
        track_name: (state.currentTrack?.name || '').slice(0, 120),
      }, state.host_id);
      failedResumes.delete(vid);
      pausedSince = 0;
      try { onAutoSkip?.(); } catch { /* noop */ }
      return;
    }

    logStall('auto_resume', {
      video_id: vid,
      resume_attempt: attempts,
      paused_ms: pausedFor,
      track_name: (state.currentTrack?.name || '').slice(0, 120),
    }, state.host_id);
    forcePlay(driver);
    // Reset the pause timer — if the resume worked, playerState will flip
    // to PLAYING next tick and we're done. If it didn't, we'll re-enter
    // this branch with attempts=2, then 3, then skip.
    pausedSince = 0;
  };

  const id = createBackgroundInterval(tick, 5_000);
  return () => id.clear();
}

// ---------------------------------------------------------------------------
// 2. Wake-lock loss logger — small, instrumentation-only
// ---------------------------------------------------------------------------

const HIDDEN_LOG_THRESHOLD_MS = 500;   // fast tab-switch flicker isn't a lock

export function startWakeLockLossLogger(getState) {
  if (typeof document === 'undefined' || typeof getState !== 'function') return () => {};

  let hiddenSince = 0;
  let hiddenVideoId = null;

  const onVis = () => {
    const visible = document.visibilityState === 'visible';
    if (!visible) {
      const state = getState() || {};
      if (state.isPlaying) {
        hiddenSince = Date.now();
        hiddenVideoId = state.currentTrack?.id || null;
      } else {
        hiddenSince = 0;
        hiddenVideoId = null;
      }
      return;
    }
    // visible again
    if (!hiddenSince) return;
    const hiddenMs = Date.now() - hiddenSince;
    const wasPlayingVid = hiddenVideoId;
    hiddenSince = 0;
    hiddenVideoId = null;
    if (hiddenMs < HIDDEN_LOG_THRESHOLD_MS) return;
    logStall('wake_lock_lost', {
      hidden_ms: hiddenMs,
      video_id: wasPlayingVid || '',
    }, (getState() || {}).host_id);
  };

  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

// ---------------------------------------------------------------------------
// 3. Network drop recovery — small
// ---------------------------------------------------------------------------

export function startNetworkRecovery({ driver, getState }) {
  if (!driver || typeof getState !== 'function' || typeof window === 'undefined') return () => {};

  const onOnline = () => {
    const state = getState() || {};
    if (!state.isPlaying) return;
    const playerState = readPlayerState(driver);
    if (!RESUMABLE_STATES.has(playerState)) return;
    logStall('net_recovered_resume', {
      video_id: state.currentTrack?.id || '',
      player_state: playerState,
      track_name: (state.currentTrack?.name || '').slice(0, 120),
    }, state.host_id);
    forcePlay(driver);
  };

  window.addEventListener('online', onOnline);
  return () => window.removeEventListener('online', onOnline);
}
